<template>
  <div v-if="hasProducts" class="w-full">
    <ProductCardCollection
      :block-position
      :cta="ctaLink"
      :layout
      :product-granularity
      :products="products || []"
      provider="algolia"
      :subtitle
      :title
      :tracking="{
        productComponent: 'RefinedProducts',
        ...tracking,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import ProductCardCollection from '@backmarket/nuxt-layer-recommendation/ProductCardCollection.vue'
import { useAddProductsCacheTagHeader } from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'

import type { ContentBlockProps } from '../../models/content-block'
import { useSearchProducts } from '../../products/useSearchProducts'

export type RefinedProductsProps = RefinedProductsApiProps & ContentBlockProps

const props = withDefaults(defineProps<RefinedProductsProps>(), {
  ctaLink: undefined,
})

// This event is common to every CMS block in order to hide the block containers
// on demand
const emits = defineEmits(['error', 'reset-error'])

const {
  data: products,
  error,
  status,
} = useSearchProducts(
  {
    productQuery: props.productQuery,
  },
  {
    immediate: true,
    server: true,
  },
)

useAddProductsCacheTagHeader({
  products: products.value || [],
})

const layout = computed(() =>
  props.productLayout === 'grid' ? 'grid' : 'carousel',
)

watch(
  products,
  () => {
    if (status?.value === 'pending') return

    if (!products.value || products.value.length === 0) {
      emits('error')

      return
    }

    emits('reset-error')
  },
  // watching immediately is necessary since we await the useSearchProducts above
  // if we don't, we will not emit an error if useSearchProducts returns an empty array for products
  { immediate: true },
)

watch(
  error,
  () => {
    if (error.value) {
      emits('error')
    }
  },
  { immediate: true },
)

const hasProducts = computed(() => products.value && products.value.length > 0)
</script>
